import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { Kicker, Reveal, SectionTitle } from "@/components/Reveal";
import PageHeader from "@/components/PageHeader";
import SplitText from "@/components/motion/SplitText";
import ImageReveal from "@/components/motion/ImageReveal";
import Button from "@/components/Button";
import JoinCta from "@/components/JoinCta";
import SpotlightSection from "@/components/SpotlightSection";

function Steps({ steps }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 60%"] });
  const line = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  return (
    <ol ref={ref} className="relative md:col-span-7" data-testid="membership-steps-list">
      <span className="absolute left-[27px] top-0 h-full w-px bg-line md:left-[35px]" aria-hidden />
      <motion.span
        style={{ height: line }}
        className="absolute left-[27px] top-0 w-[2px] bg-terra md:left-[35px]"
        aria-hidden
      />
      {steps.map(([title, desc], i) => (
        <motion.li
          key={i}
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-20% 0px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="group relative flex gap-8 py-10 md:gap-12 md:py-14"
          data-testid={`membership-step-${i}`}
        >
          <span className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-pine font-display text-xl font-bold text-white shadow-[0_10px_30px_rgba(29,90,70,0.3)] transition-transform duration-500 group-hover:scale-105 md:h-[72px] md:w-[72px] md:text-2xl">
            0{i + 1}
          </span>
          <div className="pt-2">
            <h3 className="font-display text-3xl font-semibold text-ink md:text-4xl">{title}</h3>
            <p className="mt-3 max-w-md text-lg leading-relaxed text-body">{desc}</p>
          </div>
        </motion.li>
      ))}
    </ol>
  );
}

export default function Membership() {
  const c = useContent();
  return (
    <div data-testid="membership-page">
      <PageHeader
        testid="membership-header"
        kicker={c.get("membership", "kicker")}
        title={c.get("membership", "title")}
        subtitle={c.get("membership", "who")}
        image={c.get("membership", "image", "/images/program_community.jpg")}
        imageAlt="Veridian members"
      />

      {/* ELIGIBILITY */}
      <section className="bg-bone" data-testid="membership-eligibility">
        <div className="mx-auto grid max-w-[1200px] gap-10 px-5 py-14 md:grid-cols-12 md:py-16">
          <div className="md:col-span-4">
            <Kicker>Who can join</Kicker>
            <p className="mt-3 font-display text-2xl font-semibold text-ink">
              Higher-education educators and academic leaders.
            </p>
          </div>
          <ul className="flex flex-wrap gap-3 md:col-span-8 md:justify-end md:self-center">
            {c.lines("membership", "eligibility").map((e, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-center gap-2 rounded-full border border-line bg-surface px-4 py-2 font-display text-sm font-medium text-ink transition-colors duration-300 hover:border-pine hover:bg-pine hover:text-white"
                data-testid={`eligibility-${i}`}
              >
                <Check className="h-4 w-4 text-terra" /> {e}
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* STEPS */}
      <section className="bg-surface" data-testid="membership-steps">
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-12 px-5 py-20 md:grid-cols-12 md:py-28">
          <div className="md:col-span-5">
            <div className="md:sticky md:top-32">
              <Reveal>
                <Kicker>How it works</Kicker>
              </Reveal>
              <SplitText
                as="h2"
                inView
                text="Four steps to your seat at the table."
                className="mt-4 font-display text-3xl font-semibold leading-[1.15] text-ink md:text-[2.4rem]"
              />
              <Reveal delay={0.2}>
                <p
                  className="mt-8 rounded-r-[10px] border-l-[5px] border-pine bg-bone px-6 py-5 font-display text-xl font-medium text-ink"
                  data-testid="membership-colleges-note"
                >
                  {c.get("membership", "colleges_note")}
                </p>
                <div className="mt-10">
                  <Button to="/contact" data-testid="membership-apply-button">
                    {c.get("hero", "primary_cta", "Become a member")}
                  </Button>
                </div>
              </Reveal>
            </div>
          </div>
          <Steps steps={c.pairs("membership", "steps")} />
        </div>
      </section>

      <SpotlightSection tint={false} />

      {/* CTA */}
      <section className="relative overflow-hidden bg-pine text-white" data-testid="membership-cta">
        <ImageReveal
          src="/images/program_thought.jpg"
          alt=""
          className="absolute inset-0 h-full opacity-30"
          parallax={8}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-pine via-pine/80 to-pine/30" />
        <div className="relative mx-auto max-w-[1200px] px-5 py-24 md:py-36">
          <Reveal>
            <img src="/brand/leaf.png" alt="" aria-hidden className="h-20 w-20 object-contain md:h-28 md:w-28" />
          </Reveal>
          <SectionTitle light className="mt-8 max-w-3xl md:text-5xl">
            {c.get("join", "title")}
          </SectionTitle>
          <Reveal delay={0.2} className="mt-12">
            <Button to="/contact" variant="gold" data-testid="membership-cta-button">
              Start your application
            </Button>
          </Reveal>
        </div>
      </section>

      <JoinCta />
    </div>
  );
}
